<template>
  <div class="evaluation">
    <b-row v-if="!token" class="mb-3">
      <b-col cols="12">
        <b-button variant="primary" @click="generateToken"
          ><i class="fas fa-qrcode mr-2" />{{
            $t("actions.generate_token")
          }}</b-button
        >
        <b-button variant="default" class="ml-3" @click="ladeDaten"
          ><i class="fas fa-sync"
        /></b-button>
        <b-link
          href="/mitarbeiterverwaltun/mitarbeiterbewertung/"
          target="_top"
          class="btn btn-default ml-3"
          ><i class="fas fa-list mr-3" />{{ $t("actions.to_list") }}</b-link
        >
      </b-col>
      <b-col cols="12" class="mt-3">
        <b-alert
          show
          variant="warning"
          v-html="$t('formular.warn_readonly')"
        ></b-alert>
      </b-col>
    </b-row>

    <bx-box variant="gray" header-class="col-md-8" class="mb-3">
      <template #header>
        <h3>{{ formular_text("headline") }}</h3>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="4">
              <b-form-group :label="formular_text('name')">
                <b-input
                  type="text"
                  disabled
                  :value="employee | display_name"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="formular_text('interval')"
                :state="input_state('period_start')"
                :invalid-feedback="formular_text('interval_invalid')"
              >
                <b-input
                  type="date"
                  v-model="period_start_string"
                  :state="input_state('period_start')"
                  :disabled="isSigned() || readonly"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="formular_text('interval_2')"
                :state="input_state('period_end')"
                :invalid-feedback="formular_text('interval_2_invalid')"
              >
                <b-input
                  type="date"
                  v-model="period_end_string"
                  :state="input_state('period_end')"
                  :disabled="isSigned() || readonly"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="formular_text('operation_part')"
                :state="input_state('operation_part')"
                :invalid-feedback="formular_text('operation_part_invalid')"
              >
                <v-select
                  :options="operating_parts"
                  label="title"
                  :reduce="(o) => o.id"
                  v-model="operating_part_id"
                  :class="{
                    'is-invalid': input_state('operation_part') === false,
                  }"
                  :disabled="isSigned() || readonly"
                  :clearable="false"
                  :searchable="false"
                ></v-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box
      v-for="c in evaluation_categories"
      :key="c.id"
      variant="gray"
      header-class="col-md-6"
      class="mb-3"
    >
      <template #header>
        <h4>{{ c.title }}</h4>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>

          <b-row>
            <b-col
              cols="12"
              v-for="q in questions.filter((q) => q.category_id === c.id)"
              :key="q.id"
            >
              <h6>{{ q.title }}</h6>
              <p>{{ q.question }}</p>
              <b-row>
                <b-col
                  v-for="a in answers"
                  :key="a.id"
                  cols="4"
                  md="2"
                  class="text-center"
                >
                  <b-form-radio
                    :id="`${q.id}_${a.id}`"
                    :name="q.id"
                    :value="a.id"
                    class="text-center"
                    v-model="given_answers[q.id]"
                    :state="answerState(q.id)"
                    :disabled="isSigned() || readonly"
                    size="lg"
                  ></b-form-radio>
                  <label :for="`${q.id}_${a.id}`">{{ a.title }}</label>
                </b-col>
              </b-row>
              <hr />
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" class="mb-3">
      <bx-box variant="white" class="mb-1" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="12">
              <b-form-group :label="formular_text('comments')">
                <b-textarea
                  v-model="comment"
                  rows="3"
                  :disabled="isSigned() || readonly"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="formular_text('date')">
                <b-input
                  type="date"
                  v-model="date"
                  :disabled="isSigned() || readonly"
                />
              </b-form-group>
            </b-col>
            <b-col md="8">
              <h6>
                {{
                  $t("formular.employee.evaluation.signature", {
                    name: evaluator
                      ? evaluator.name
                      : formular_text("bewerter"),
                  })
                }}
              </h6>
              <signature-pad
                ref="signature"
                height="200px"
                v-model="signature"
                :invalid-feedback="formular_text('signature_invalid')"
                :state="input_state('signature')"
                :disabled="isSigned() || readonly"
              ></signature-pad>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <b-button
      v-if="!id"
      variant="primary"
      class="w-100"
      :disabled="(errors_activated && $v.$invalid) || isSigned()"
      @click="speichern"
    >
      {{ $t("actions.save") }}
    </b-button>

    <token-invalid-modal ref="tokenInvalidModal"></token-invalid-modal>
    <speichern-erfolgreich-modal
      ref="speichernErfolgreichModal"
    ></speichern-erfolgreich-modal>
    <unexpected-error-modal ref="unexpectedErrorModal"></unexpected-error-modal>
    <validation-failure-modal
      ref="validationFailureModal"
    ></validation-failure-modal>
  </div>
</template>

<script>
  import formular from "@m/formular";
  import api from "@a/index";
  import { DateTime } from "luxon";
  import EventBus from "@/event-bus.js";
  import { OPEN_ACCESS_LAYER } from "@/event-bus";
  import { formularKeys } from "@/services/formulars/formularKeyDecoder";
  import SignaturePad from "@c/SignaturePad.vue";
  import _ from "lodash";
  import { required } from "vuelidate/lib/validators";

  export default {
    name: "Evaluation",
    metaInfo() {
      const title = this.formular_text("headline");
      return {
        titleTemplate: () => {
          return title;
        },
      };
    },
    components: {
      SignaturePad,
    },
    mixins: [formular],
    data() {
      return {
        answers: [],
        comment: null,
        date: null,
        employee: null,
        employee_id: null,
        evaluation_categories: [],
        evaluator: null,
        evaluator_id: null,
        given_answers: null,
        operating_part: null,
        operating_part_id: null,
        operating_parts: [],
        period_end: null,
        period_start: null,
        questions: [],
      };
    },
    validations() {
      return {
        period_start: {
          required,
        },
        period_end: {
          required,
        },
        operating_part_id: {
          required,
        },
      };
    },
    computed: {
      formular_key() {
        return "employee.evaluation";
      },
      period_start_string: {
        get() {
          return this.period_start
            ? DateTime.fromMillis(this.period_start).toFormat("yyyy-MM-dd")
            : null;
        },
        set(val) {
          this.period_start = val
            ? DateTime.fromFormat(val, "dd-MM-yyyy").ts
            : null;
        },
      },
      period_end_string: {
        get() {
          return this.period_end
            ? DateTime.fromMillis(this.period_end).toFormat("yyyy-MM-dd")
            : null;
        },
        set(val) {
          this.period_end = val
            ? DateTime.fromFormat(val, "dd-MM-yyyy").ts
            : null;
        },
      },
    },
    async created() {
      await this.ladeDaten();
      this.loading = false;
    },
    methods: {
      async ladeDaten() {
        let loader = this.$loading.show({});
        try {
          let response = this.id
            ? await api.privateFormular.employee.evaluation.getById(this.id)
            : this.token
            ? await api.publicFormluar.employee.evaluation.get(this.token)
            : null;

          this.initialize(response?.data);
        } catch (e) {
          console.error(e);
          if (e.response.status === 401) {
            this.$refs.tokenInvalidModal.show();
          }
        } finally {
          this.loading = false;
          loader.hide();
        }
      },
      async speichern() {
        if (this.$v.$invalid) {
          this.errors_activated = true;
          this.$refs.validationFailureModal.show();
        } else {
          let loader = this.$loading.show({});

          try {
            let json = this.toJson();
            await api.publicFormluar.employee.evaluation.post(json, this.token);

            this.$refs.speichernErfolgreichModal.show();
          } catch (e) {
            console.error(e);
            if (e.response?.status === 401) {
              this.$refs.tokenInvalidModal.show();
            } else {
              this.$refs.unexpectedErrorModal.show();
            }
          } finally {
            loader.hide();
          }
        }
      },
      // eslint-disable-next-line no-unused-vars

      initializeCategories(categories) {
        this.evaluation_categories = categories.categories;
        this.questions = categories.questions;
        this.answers = categories.answers;
        this.operating_parts = categories.operating_parts;

        let given_answers = {};

        this.questions.forEach((q) => {
          given_answers[q.id] = null;
        });

        this.given_answers = given_answers;
      },
      initializeEvaluation(evaluation) {
        _.toPairs(evaluation)
          // eslint-disable-next-line no-unused-vars
          .filter(([key, _value]) => key !== "id" && key !== "answers")
          .forEach(([key, value]) => {
            this[key] = value;
          });

        evaluation.answers.forEach((a) => {
          if (this.given_answers[a.question_id] !== undefined) {
            this.given_answers[a.question_id] = a.answer_id;
          }
        });

        this.date = evaluation.date
          ? DateTime.fromMillis(evaluation.date).toFormat("yyyy-MM-dd")
          : DateTime.local().toFormat("yyyy-MM-dd");
      },
      toJson() {
        return {
          signature: this.signature,
          employee_id: this.employee_id,
          comment: this.comment,
          date: null,
          evaluator_id: this.evaluator_id,
          operating_part_id: this.operating_part_id,
          period_start: this.period_start,
          period_end: this.period_end,
          answers: _.toPairs(this.given_answers).map(([key, value]) => {
            return {
              question_id: key,
              answer_id: value,
            };
          }),
        };
      },
      initialize(responseData) {
        if (responseData) {
          this.initializeCategories(responseData.categories);
          if (responseData.data) {
            this.initializeEvaluation(responseData.data);
          }
        }
      },
      generateToken() {
        EventBus.$emit(
          OPEN_ACCESS_LAYER,
          formularKeys.evaluation,
          "",
          DateTime.local().plus({ days: 1 }),
          this.employee_id,
          this.evaluator_id,
          this.operating_part_id,
          this.id
        );
      },
      answerState(question) {
        if (this.errors_activated && this.$v?.given_answers[question]?.$invalid)
          return false;
        return null;
      },
    },
  };
</script>

<style lang="scss">
</style>
